import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { WARRANTY_TYPES, formatDate, daysUntil } from "@/lib/dict";
import { usePickLists } from "@/hooks/usePickLists";

export default function Warranties() {
  const pl = usePickLists();
  const [filters, setFilters] = useState({ client_id: "all", legal_entity_id: "all", asset_id: "all" });

  const { data: warranties = [] } = useQuery({ queryKey: ["warranties"], queryFn: () => api.get("/warranties").then((r) => r.data) });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => api.get("/assets").then((r) => r.data) });
  const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => api.get("/clients").then((r) => r.data) });
  const { data: legalEntities = [] } = useQuery({ queryKey: ["legal-entities"], queryFn: () => api.get("/legal-entities").then((r) => r.data) });

  const am = useMemo(() => Object.fromEntries(assets.map((a) => [a.id, a])), [assets]);
  const legalEntitiesForClient = useMemo(() => (
    filters.client_id === "all" ? legalEntities : legalEntities.filter((le) => le.client_id === filters.client_id)
  ), [legalEntities, filters.client_id]);
  const assetsForScope = useMemo(() => assets.filter((a) => {
    if (filters.client_id !== "all" && a.client_id !== filters.client_id) return false;
    if (filters.legal_entity_id !== "all" && a.legal_entity_id !== filters.legal_entity_id) return false;
    return true;
  }), [assets, filters.client_id, filters.legal_entity_id]);

  const filtered = useMemo(() => warranties.filter((w) => {
    if (filters.asset_id !== "all") return w.asset_id === filters.asset_id;
    if (filters.legal_entity_id !== "all" || filters.client_id !== "all") {
      const a = am[w.asset_id];
      if (!a) return false;
      if (filters.legal_entity_id !== "all" && a.legal_entity_id !== filters.legal_entity_id) return false;
      if (filters.client_id !== "all" && a.client_id !== filters.client_id) return false;
    }
    return true;
  }), [warranties, filters, am]);

  const active = filters.client_id !== "all" || filters.legal_entity_id !== "all" || filters.asset_id !== "all";

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Module</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mt-1">Garanties</h1>
        <p className="text-sm text-slate-500 mt-1.5">{filtered.length} garantie(s) — GPA, GBF, Décennale, DO</p>
      </div>

      {/* Scope filters */}
      <Card className="p-3 border-slate-200">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={filters.client_id} onValueChange={(v) => setFilters({ ...filters, client_id: v, legal_entity_id: "all", asset_id: "all" })}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous clients</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.legal_entity_id} onValueChange={(v) => setFilters({ ...filters, legal_entity_id: v, asset_id: "all" })}>
            <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="filter-legal-entity"><SelectValue placeholder="Entité juridique" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes entités</SelectItem>
              {legalEntitiesForClient.map((le) => <SelectItem key={le.id} value={le.id}>{le.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.asset_id} onValueChange={(v) => setFilters({ ...filters, asset_id: v })}>
            <SelectTrigger className="w-[180px] h-9 text-sm" data-testid="filter-asset"><SelectValue placeholder="Site" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous sites</SelectItem>
              {assetsForScope.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          {active && (
            <Button
              data-testid="reset-filters"
              variant="ghost" size="sm" className="h-9 text-xs text-slate-600"
              onClick={() => setFilters({ client_id: "all", legal_entity_id: "all", asset_id: "all" })}
            >Réinitialiser</Button>
          )}
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Type</th>
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Actif</th>
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Début</th>
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Fin</th>
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Reste</th>
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Entreprise</th>
            <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500">Statut</th>
          </tr></thead>
          <tbody>
            {filtered.map((w) => {
              const days = daysUntil(w.end_date);
              const tone = days == null ? "text-slate-500" : days < 0 ? "text-red-600" : days <= 30 ? "text-red-600" : days <= 90 ? "text-amber-600" : "text-slate-700";
              return (
                <tr key={w.id} data-testid={`warranty-${w.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold">{pl.labelOf("warranty.type", w.warranty_type, WARRANTY_TYPES)}</td>
                  <td className="px-4 py-3">{am[w.asset_id]?.name}</td>
                  <td className="px-4 py-3 text-xs">{formatDate(w.start_date)}</td>
                  <td className="px-4 py-3 text-xs">{formatDate(w.end_date)}</td>
                  <td className={`px-4 py-3 text-xs font-semibold ${tone}`}>{days == null ? "—" : days < 0 ? `Expirée (J+${-days})` : `J-${days}`}</td>
                  <td className="px-4 py-3 text-xs">{w.contractor || "—"}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className={w.status === "active" ? "bg-emerald-50 text-emerald-700" : w.status === "expiring" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}>{w.status}</Badge></td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400 text-sm">Aucune garantie sur cette portée.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
